import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function FinancialsAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Liabilities - Series API" description="Query liabilities data via the Series API; request parameters, code examples and the response schema for the liabilities endpoint."  canonicalUrl="https://axionquant.com/docs/03-fundamentals/financials/series/liabilities" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Liabilities - Series API</DocHeading>

      <div className="space-y-12" ref={sectionRef}>
        {/* ========== 4. Total Liabilities ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /financials/:ticker/total/liabilities
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Returns total liabilities as reported on the balance sheet at fiscal year-end.
              </p>
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm"><span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">Required</span></td>
                      <td className="px-4 py-3 text-sm text-gray-700">Stock ticker symbol</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">periods</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm"><span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">Optional</span></td>
                      <td className="px-4 py-3 text-sm text-gray-700">Number of fiscal years to return (default: 1).</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/financials/AAPL/total/liabilities', '/financials/AAPL/total/liabilities?periods=3']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response</h3>
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-mono text-sm text-gray-800 mb-1">[number, number, ...]</p>
                <p className="text-sm text-gray-600">Year-end total liabilities.</p>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Total Liabilities (Apple, 4 periods)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Total Liabilities (Apple, 4 periods)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="financials/ticker/total/liabilities"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="03-fundamentals/financials/liabilities"></Code>
              </div>
            </div>
          </div>
        </div>

        {/* ========== 7. Current Liabilities ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /financials/:ticker/current/liabilities
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Returns current liabilities (obligations due within one year) at fiscal year-end.
              </p>
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm"><span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">Required</span></td>
                      <td className="px-4 py-3 text-sm text-gray-700">Stock ticker symbol</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">periods</td>
                      <td className="px-4 py-3 text-sm text-gray-700">integer</td>
                      <td className="px-4 py-3 text-sm"><span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">Optional</span></td>
                      <td className="px-4 py-3 text-sm text-gray-700">Number of fiscal years to return (default: 1).</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/financials/AAPL/current/liabilities', '/financials/AAPL/current/liabilities?periods=3']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response</h3>
              <div className="border-l-4 border-blue-500 pl-4">
                <p className="font-mono text-sm text-gray-800 mb-1">[number, number, ...]</p>
                <p className="text-sm text-gray-600">Year-end current liabilities.</p>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Current Liabilities (Apple, 4 periods)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Current Liabilities (Apple, 4 periods)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="financials/ticker/current/liabilities"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="03-fundamentals/financials/liabilities-1"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
